import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { CalendarClock, Info } from "lucide-react";
import { PageHeader, Panel } from "@/components/app/ui";
import { JourneyBar } from "@/components/app/JourneyBar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";

const CARDS = [
  ["applicable_controls", "Applicable Controls", "text-slate-900"], ["controls_assessed", "Controls Assessed", "text-slate-900"],
  ["compliant", "Compliant", "text-emerald-700"], ["partial", "Partial", "text-amber-700"], ["non_compliant", "Non-Compliant", "text-rose-700"],
  ["evidence_pending", "Evidence Pending", "text-indigo-700"], ["high_critical_findings", "High/Critical Findings", "text-orange-700"],
  ["future_effective", "Future Effective", "text-purple-700"], ["legal_review", "Legal Review Required", "text-sky-700"],
];
const SERIES = [["Compliant", "#10B981"], ["Partially Compliant", "#F59E0B"], ["Non-Compliant", "#F43F5E"], ["Evidence Required", "#6366F1"],
  ["Requires Legal Review", "#0EA5E9"], ["Future Effective", "#A855F7"], ["Not Assessed", "#CBD5E1"]];
const LEVELS = ["Critical", "High", "Medium", "Low", "Informational"];
const heatCls = (lv, n) => {
  if (!n) return "bg-slate-50 text-slate-300";
  return { Critical: "bg-rose-500 text-white", High: "bg-orange-400 text-white", Medium: "bg-amber-300 text-amber-950", Low: "bg-slate-300 text-slate-800", Informational: "bg-slate-100 text-slate-600" }[lv];
};

function Readiness({ rows }) {
  return (
    <Panel title="Regulatory Readiness — phased DPDP commencement" testId="readiness-panel"
      actions={<Link to="/regulatory" className="text-xs text-blue-600 hover:underline">View library</Link>}>
      <div className="grid gap-4 md:grid-cols-3">
        {rows.map((r) => (
          <div key={r.phase} data-testid={`readiness-${r.phase}`} className={`rounded-lg border p-4 ${r.effective ? "border-blue-200 bg-blue-50/60" : "border-purple-200 bg-purple-50/50"}`}>
            <div className="flex items-center justify-between">
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${r.effective ? "text-blue-700" : "text-purple-700"}`}>{r.effective ? "Currently effective" : "Upcoming"}</span>
              <CalendarClock className="h-4 w-4 text-slate-400" />
            </div>
            <div className="mt-2 font-display text-sm font-semibold text-slate-900">{r.label}</div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-display text-3xl font-extrabold text-slate-900" data-testid={`readiness-days-${r.phase}`}>{r.effective ? "In force" : r.days_until}</span>
              {!r.effective && <span className="text-xs text-slate-500">days until effective</span>}
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded bg-white/80 py-1.5"><div className="font-semibold text-slate-900">{r.controls}</div><div className="text-slate-500">controls</div></div>
              <div className="rounded bg-white/80 py-1.5"><div className="font-semibold text-emerald-700">{r.compliant}</div><div className="text-slate-500">compliant</div></div>
              <div className="rounded bg-white/80 py-1.5"><div className="font-semibold text-rose-700">{r.gaps}</div><div className="text-slate-500">gaps</div></div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Heatmap({ rows, testId }) {
  if (!rows.length) return <p className="py-6 text-center text-sm text-slate-400">No open findings.</p>;
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs">
        <thead><tr><th className="py-1 text-left font-medium text-slate-500">Area</th>{LEVELS.map((l) => <th key={l} className="px-1 py-1 font-medium text-slate-500">{l}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td className="max-w-[180px] truncate py-1 pr-2 text-slate-700">{r.name}</td>
              {LEVELS.map((l) => <td key={l} className="p-0.5"><div className={`grid h-8 place-items-center rounded font-semibold ${heatCls(l, r[l])}`}>{r[l] || "·"}</div></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const TEAM_COLS = [["open", "Open", "text-slate-900"], ["overdue", "Overdue", "text-red-700"], ["no_target", "No target date", "text-amber-700"],
  ["after_deadline", "Due after 13 May 2027", "text-purple-700"], ["on_track", "On track", "text-emerald-700"], ["high_priority", "P1 priority", "text-rose-700"]];

function TeamReadiness({ tr }) {
  return (
    <Panel title="Team readiness — open remediation vs 13 May 2027" testId="team-readiness-panel"
      actions={<span className="rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-semibold text-purple-700" data-testid="team-readiness-days">{tr.days_until} days left</span>}>
      {tr.teams.length ? (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left"><th className="label-caps pb-2">Team / owner</th>{TEAM_COLS.map(([k, l]) => <th key={k} className="label-caps pb-2 text-center">{l}</th>)}<th className="label-caps w-48 pb-2">Status mix</th></tr></thead>
          <tbody>{tr.teams.map((t) => (
            <tr key={t.team} className="border-t" data-testid={`team-row-${t.team.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <td className="py-2 font-medium text-slate-800">{t.team}</td>
              {TEAM_COLS.map(([k, , c]) => <td key={k} className={`py-2 text-center font-display font-bold ${t[k] ? c : "text-slate-300"}`}>{t[k]}</td>)}
              <td className="py-2"><div className="flex h-2 overflow-hidden rounded-full bg-slate-100">
                {[["overdue", "bg-red-500"], ["no_target", "bg-amber-400"], ["after_deadline", "bg-purple-500"], ["on_track", "bg-emerald-500"]].map(([k, c]) => t[k] ? <div key={k} className={c} style={{ width: `${(t[k] / t.open) * 100}%` }} title={`${k}: ${t[k]}`} /> : null)}
              </div></td>
            </tr>))}</tbody>
        </table></div>
      ) : <p className="text-sm text-slate-400">No open remediation items.</p>}
      <p className="mt-3 text-xs text-slate-500">Items with no target date or a target after 13 May 2027 may not be ready when the main operational provisions take effect. <Link to="/remediation" className="text-blue-600 hover:underline">Set target dates</Link></p>
    </Panel>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-sm text-slate-400">Loading dashboard…</div>;
  return (
    <div className="space-y-6">
      <PageHeader title="DPDP Compliance Posture" subtitle="Control-level posture across applicable DPDP requirements. No aggregate compliance percentage is shown by design." />
      <JourneyBar />
      <div data-testid="posture-statement" className="flex items-start gap-2 rounded-md border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600"><Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />{d.statement}</div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-9">
        {CARDS.map(([k, label, cls], i) => (
          <div key={k} data-testid={`stat-${k}`} className="rounded-lg border border-slate-200 bg-white p-4 fade-up" style={{ animationDelay: `${i * 30}ms` }}>
            <div className="text-[11px] font-medium leading-tight text-slate-500">{label}</div>
            <div className={`mt-2 font-display text-2xl font-extrabold ${cls}`}>{d.cards[k]}</div>
          </div>
        ))}
      </div>
      <Readiness rows={d.readiness} />
      <TeamReadiness tr={d.team_readiness} />
      <div className="grid gap-6 lg:grid-cols-5">
        <Panel title="Compliance posture by domain" className="lg:col-span-3" testId="domain-posture-chart">
          <div style={{ height: Math.max(260, d.domains.length * 30) }}>
            <ResponsiveContainer>
              <BarChart data={d.domains} layout="vertical" margin={{ left: 10, right: 10 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="domain" width={170} tick={{ fontSize: 11 }} />
                <Tooltip wrapperStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {SERIES.map(([k, c]) => <Bar key={k} dataKey={k} stackId="a" fill={c} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <div className="space-y-6 lg:col-span-2">
          <Panel title="Remediation" testId="remediation-summary" actions={<Link to="/remediation" className="text-xs text-blue-600 hover:underline">Open tracker</Link>}>
            <div className="grid grid-cols-4 gap-2 text-center">
              {[["open", "Open", "text-rose-700"], ["overdue", "Overdue", "text-orange-700"], ["in_progress", "In progress", "text-indigo-700"], ["closed", "Closed", "text-emerald-700"]].map(([k, l, c]) => (
                <div key={k} data-testid={`rem-${k}`} className="rounded-md bg-slate-50 py-3"><div className={`font-display text-xl font-extrabold ${c}`}>{d.remediation[k]}</div><div className="text-[11px] text-slate-500">{l}</div></div>
              ))}
            </div>
          </Panel>
          <Panel title="Risk heatmap (open findings)" testId="risk-heatmap">
            <Tabs defaultValue="domain">
              <TabsList className="mb-3"><TabsTrigger value="domain" data-testid="heatmap-tab-domain">Domain</TabsTrigger><TabsTrigger value="system" data-testid="heatmap-tab-system">System</TabsTrigger><TabsTrigger value="business_unit" data-testid="heatmap-tab-bu">Business unit</TabsTrigger></TabsList>
              {["domain", "system", "business_unit"].map((k) => <TabsContent key={k} value={k}><Heatmap rows={d.heatmap[k]} testId={`heatmap-${k}`} /></TabsContent>)}
            </Tabs>
          </Panel>
        </div>
      </div>
    </div>
  );
}
